<script context="module" lang="ts">
	//Enums
	export enum StoreApplicationStatus {
		ALL = "ALL",
		PENDING = "PENDING",
		DRAFT = "DRAFT",
		APPROVED = "APPROVED",
		TERMINATED = "TERMINATED",
	}

	export enum StoreStatus {
		ALL = "ALL",
		UNKNOWN = "UNKNOWN",
		OPEN = "OPEN",
		CLOSED = "CLOSED",
		BREAK = "BREAK",
	}
</script>

<script lang="ts">
	//UI
	import { AutoComplete, Card, Pagenation } from "@hitit/new_ui/src";
	import ContainerLoading from "@hitit/hub/src/v7/components/common/ContainerLoading.svelte";
	import FilterOption from "../component/Search/FilterOption.svelte";
	import SearchStoreResult, {
		TableColumn,
	} from "../component/Search/SearchStoreResult.svelte";
	//Utils
	import queryString from "query-string";
	import { t, api } from "@hitit/hub/src/stores";
	import { navigate } from "svelte-routing";
	import { translateCount } from "@hitit/translations";
	import { globalHistory } from "svelte-routing/src/history";
	//Types
	import type { StoreSearchResult } from "@hitit/api";
	import type { SortOrder } from "../component/Search/SearchStoreResult.svelte";
	import { onDestroy } from "svelte";
	import { popup } from "@hitit/hub/src/v7/store";

	type FilterOption = {
		storeApplicationStatus: StoreApplicationStatus;
		storeStatus: StoreStatus;
	};
	type locationOverload = { search: string; pathname: string };

	//Props
	//Constants
	const theFirstQuery = parseQuerystring(window.location.search);
	const divideCountOption = new Array(10)
		.fill(null) // 10 ~ 100
		.map((_, i) => i + 1)
		.map((v) => v * 10);
	const storeApplicationStatusOption = Object.keys(StoreApplicationStatus);
	const storeStatusOption = Object.keys(StoreStatus);

	//Variables
	let loading = true;
	let searchValue = "";
	let searchText = theFirstQuery.search;
	let page: number = theFirstQuery.page;
	let divideCount = theFirstQuery.divide;
	let selectedStoreApplicationStatus: StoreApplicationStatus =
		theFirstQuery.storeApplicationStatus;
	let selectedStoreStatus: StoreStatus = theFirstQuery.storeStatus;
	let sortOrder: SortOrder = theFirstQuery.sortOrder;
	let selectedSortColumn: TableColumn = theFirstQuery.sortColumn;
	let stores: StoreSearchResult[] = [];

	$: filterOption = {
		storeApplicationStatus: selectedStoreApplicationStatus,
		storeStatus: selectedStoreStatus,
	};
	$: maxPage =
		divideCount !== 0
			? Math.ceil(filterStores(stores, filterOption).length / divideCount)
			: 0;
	$: totalCount = filterStores(stores, filterOption).length;
	$: autoCompleteProps = {
		placeholder: $t("mbr.search.search_storename_address"),
		searchCallback: autocompleteHandler,
	};
	$: sortedStores = sort(stores, selectedSortColumn, sortOrder);
	$: filteredStores = filterStores(sortedStores, filterOption);
	$: dividedStores = divideStores(filteredStores, divideCount, page);

	//Functions
	async function autocompleteHandler(search: string) {
		try {
			const { data } = await $api.getStorelistByStorename(search, 0);
			const mappedResult = data.searchResults.map((v) => ({
				name: v.name,
				callback: () => navigate(`/store/${v.id}/`),
			}));
			return mappedResult;
		} catch (e) {
			console.log("error");
			return [];
		}
	}

	async function getAllStoreByName(search = "") {
		try {
			loading = true;
			const { searchResults, totalPages } = (
				await $api.getStorelistByStorename(search, 0)
			).data;

			if (totalPages === 0) {
				resetAll();
				return;
			}

			const dataPromise = new Array(totalPages - 1).fill(null).map((_, i) => {
				return $api.getStorelistByStorename(search, i + 1);
			});
			const afterPageOneStore = (await Promise.all([...dataPromise]))
				.map((v) => {
					return v.data.searchResults;
				})
				.flat(1);

			const allStore = [...searchResults, ...afterPageOneStore];
			// resetAll();
			stores = allStore;
		} catch (e) {
			console.log(e);
		} finally {
			loading = false;
		}
	}

	function resetAll() {
		stores = [];
		page = 0;
	}
	function filterStores(
		stores: StoreSearchResult[],
		filterOption: FilterOption
	) {
		let __stores = [...stores];
		if (filterOption.storeApplicationStatus !== StoreApplicationStatus.ALL) {
			__stores = __stores.filter(
				(v) => v.storeApplicationStatus === filterOption.storeApplicationStatus
			);
		}

		if (filterOption.storeStatus !== StoreStatus.ALL) {
			__stores = __stores.filter(
				(v) => v.storeStatus === filterOption.storeStatus
			);
		}

		return __stores;
	}
	function sort(list: StoreSearchResult[], column: TableColumn, by: SortOrder) {
		if (by === "none") return list;
		let compare: any;

		const compareFuncs = {
			asc: (a: any, b: any) => (a < b ? -1 : b < a ? 1 : 0),
			desc: (a: any, b: any) => (a > b ? -1 : b > a ? 1 : 0),
		};

		compare = compareFuncs[by];
		return [...list].sort((a, b) => {
			if (column === TableColumn.createDate) {
				const A = new Date(a.createdDate);
				const B = new Date(b.createdDate);

				return compare(A, B);
			} else {
				return compare(
					a[column as keyof StoreSearchResult],
					b[column as keyof StoreSearchResult]
				);
			}
		});
	}
	function undefinedArray(length: number) {
		return new Array(length).fill(undefined);
	}
	function divideStores(
		stores: StoreSearchResult[],
		divideCount: number,
		currentPage: number
	) {
		const start = divideCount * currentPage;
		const end = start + divideCount;
		return stores.slice(start, end);
	}

	function enumrateSortBy(sortBy: SortOrder) {
		if (sortBy === "none") return "asc";
		else if (sortBy === "asc") return "desc";
		else return "none";
	}

	function sortClickHandler(selected: TableColumn) {
		const _newsortOrder =
			selected === selectedSortColumn ? enumrateSortBy(sortOrder) : "asc";
		navigateContorller(
			undefined,
			undefined,
			undefined,
			undefined,
			undefined,
			selected,
			_newsortOrder
		);
	}
	function rowClickHandler(storeId: string) {
		navigate(`/store/${storeId}/`);
	}
	function parseQuerystring(query: string) {
		const parsed = queryString.parse(query);
		const {
			search,
			page,
			divide,
			storeStatus,
			applicationStatus,
			sortColumn,
			sortOrder,
		} = parsed;

		const dividedVerification = new Array(10)
			.fill(null)
			.map((_, i) => (i + 1) * 10)
			.find((v) => v === Number(divide));

		const storeStatusVerification = Object.values(StoreStatus).find(
			(v) => v === storeStatus
		);
		const storeApplicationSttatusVerification = Object.values(
			StoreApplicationStatus
		).find((v) => v === applicationStatus);
		const sortColumnVerification = Object.values(TableColumn).find(
			(v) => v === sortColumn
		);
		const sortOrderVerification = ["none", "asc", "desc"].find(
			(v) => v === sortOrder
		);

		return {
			search: (search || "") as string,
			page: Number(page) || 0,
			divide: dividedVerification ? dividedVerification : 20,
			storeStatus: storeStatusVerification
				? storeStatusVerification
				: StoreStatus.ALL,
			storeApplicationStatus: storeApplicationSttatusVerification
				? storeApplicationSttatusVerification
				: StoreApplicationStatus.ALL,
			sortColumn: sortColumnVerification
				? sortColumnVerification
				: TableColumn.none,
			sortOrder: (sortOrderVerification
				? sortOrderVerification
				: "none") as SortOrder,
		};
	}

	function navigateContorller(
		_searchText = searchText,
		_page = page,
		_divideCount = divideCount,
		_selectedStoreStatus = selectedStoreStatus,
		_storeApplicationStatus = selectedStoreApplicationStatus,
		_selectedSortColumn = selectedSortColumn,
		_sortOrder = sortOrder
	) {
		const searchQuery = `search=${_searchText}&`;
		const pageQuery = `page=${_page}&`;
		const divideQuery = `divide=${_divideCount}&`;
		const storeStatusQuery = `storeStatus=${_selectedStoreStatus}&`;
		const applicationStatusQuery = `applicationStatus=${_storeApplicationStatus}&`;
		const sortColumnQuery = `sortColumn=${_selectedSortColumn}&`;
		const sortOrder = `sortOrder=${_sortOrder}`;

		const query =
			searchQuery +
			pageQuery +
			divideQuery +
			storeStatusQuery +
			applicationStatusQuery +
			sortColumnQuery +
			sortOrder;
		navigate(`/store?` + query);
	}
	//Do

	$: getAllStoreByName(searchText);
	$: if (page > maxPage && !loading) {
		const newPage = maxPage - 1 < 0 ? 0 : maxPage - 1;
		page = newPage;
	}

	onDestroy(() => {
		$popup = undefined;
	});

	globalHistory.listen((v) => {
		const query = parseQuerystring((v.location as locationOverload).search);
		searchText = query.search;
		page = query.page;
		divideCount = query.divide;
		selectedStoreApplicationStatus = query.storeApplicationStatus;
		selectedStoreStatus = query.storeStatus;
		selectedSortColumn = query.sortColumn;
		sortOrder = query.sortOrder as SortOrder;
	});
</script>

{#if loading}
	<ContainerLoading />
{/if}
<div class="container">
	<form
		class="search-box"
		on:submit|preventDefault={() => navigateContorller(searchValue, 0)}
	>
		<AutoComplete {...autoCompleteProps} bind:search={searchValue} />
	</form>
	<Card noPadding>
		<div class="search-container">
			<div class="total-filteroption">
				<div class="total">
					{translateCount($t("global.total_{count}_ea"), totalCount)}
				</div>
				<div class="filteroption">
					<FilterOption
						on:change={(e) =>
							navigateContorller(...undefinedArray(2), e.detail)}
						value={divideCount}
						items={divideCountOption}
						label={$t("mbr.search.display_count")}
					/>
					<FilterOption
						on:change={(e) =>
							navigateContorller(...undefinedArray(4), e.detail)}
						value={selectedStoreApplicationStatus}
						label={$t("mbr.search.store_status")}
						items={storeApplicationStatusOption}
					/>
					<FilterOption
						on:change={(e) =>
							navigateContorller(...undefinedArray(3), e.detail)}
						value={selectedStoreStatus}
						items={storeStatusOption}
						label={$t("mbr.search.application_status")}
					/>
				</div>
			</div>
			<div class="results">
				<SearchStoreResult
					rowClickHandler={rowClickHandler}
					columnHeadClickHandler={sortClickHandler}
					sortBy={selectedSortColumn}
					sortOrder={sortOrder}
					stores={dividedStores}
				/>
				<Pagenation
					totalPage={maxPage}
					presentPage={page}
					buttonCallback={(p) => navigateContorller(undefined, p)}
				/>
			</div>
		</div>
	</Card>
</div>

<style lang="scss">
	@import "../../variables.scss";

	.container {
		display: flex;
		flex-direction: column;
		gap: 20px;
		padding-top: 60px;

		.search-box {
			width: 100%;
		}

		.search-container {
			width: 100%;
			display: flex;
			flex-direction: column;
			padding: 20px 10px;
			gap: 20px;
			.results {
				display: flex;
				flex-direction: column;
				gap: 10px;
			}

			.total-filteroption {
				display: flex;
				flex-direction: column;
				gap: 10px;
				width: 100%;

				.total {
					width: 100%;
					flex-grow: 1;
					text-align: right;
					font-weight: 500;
					font-size: 1.25rem;
					color: #46474b;
				}
				.filteroption {
					display: flex;
					flex-direction: column;
					gap: 10px;
				}
			}
		}
	}

	@include DesktopSize {
		.container {
			padding-top: 30px;
			.search-box {
				width: 50%;
				:global(input) {
					padding-bottom: 3px;
				}
			}

			.search-container {
				padding: 30px 30px 35px 30px;
				gap: 30px;
				.results {
					gap: 20px;
				}

				.total-filteroption {
					flex-direction: row-reverse;
					align-items: center;
					flex-wrap: wrap;
					gap: 20px 0px;

					.total {
						width: auto;
					}

					.filteroption {
						flex-grow: 1;
						flex-direction: row;
						gap: 30px;
					}
				}
			}
		}
	}
</style>
